// Vue
import Vue from "vue";
Vue.config.productionTip = false;

// Router (VueRouter)
import router from "./router.js";

// Vue head
import VueHead from "vue-head";
Vue.use(VueHead);

// Store (vuex)
import store from "./store.js";

// NProgres
import {NProgress} from "./nprogress.js";

// Axios
import {HTTP} from "./http-common.js";

// PWA
import "./registerServiceWorker.js";

// Font Loader (FontFaceObserver)
import themeFontLoader from "./libs/font-loader.js";
themeFontLoader(["Roboto", "Roboto Mono", "Montserrat"]);

// Notifications
import Toasted from "vue-toasted";
import {toastedDefaults, toastedDefaultSuccess, toastedDefaultError} from "./libs/toasted-options.js";

Vue.use(Toasted);

Vue.toasted.register(
	"app_error",
	payload => {
		// if there is no message passed show default message
		if (!payload.message) return "Une erreur inconnue est survenue !";
		// if there is a message show it with the message
		// ❗⚠ ⚠️
		return `<div class="message-wrapper">${payload.message}</div>`;
	},
	toastedDefaultError
);

Vue.toasted.register(
	"app_success",
	payload => {
		// ✓
		return `<div class="message-wrapper">${payload.message}</div>`;
	},
	toastedDefaultSuccess
);

Vue.toasted.register(
	"app_info",
	payload => {
		return `<div class="message-wrapper">${payload.message}</div>`;
	},
	toastedDefaults
);

// Global Route Guards
// Les routes qui nécessitent une authentification ont une meta `authenticated` définie sur `true`
// Ces routes nécessitent que l'utilisateur soit **authentifié** (`store.userData` ne peut pas être indéfini)

router.beforeEach((to, from, next) => {
	// The user is logged-in, redirect from home to dashboard
	if (to.name === "home" && !!store.state.userData && parseInt(store.state.userData.registration_complete) === 1) {
		next("/dashboard");
	}

	// no authentication needed
	if (!to.meta || !to.meta.authenticated) next();
	// route needs an authenticated user
	else {
		if (!store.state.userData || !store.state.userData.registration_complete) {
			let msg = "";
			let nextRoute = "/login";

			// L'utilisateur est connecté pour terminer son inscription
			if (!!store.state.userData && !store.state.userData.registration_complete) {
				Vue.toasted.global.app_info({
					message: "Vous pouvez maintenant compléter votre profil pour terminer l'inscription."
				});
				next("/register");
				return;
			}

			// Le token est disponible mais pas les infos utilisateur : session expirée
			if (!!store.state.userToken) {
				msg = "Votre session a expirée, merci de vous connecter.";
			}
			// L'utilisateur n'est pas connecté
			else {
				msg = "Vous devez vous identifier pour accéder à cette page.";
			}

			Vue.toasted.global.app_error({
				message: msg
			});

			next(nextRoute);
		} else next();
	}
});

// Vue filters
import {slugify} from "./libs/helpers";
Vue.filter("slugify", slugify);

// Main App
import App from "./App.vue";

new Vue({
	router,
	store,
	render: h => h(App),

	head: {
		title: {
			inner: "CrossDoc"
		},
		// Meta tags
		meta: [
			{name: "msapplication-TileColor", content: "#00aba9"},
			{name: "theme-color", content: "#ffffff"}
		],
		link: [
			{
				rel: "manifest",
				href: "/manifest.json"
			},
			{
				rel: "apple-touch-icon",
				href: "/apple-touch-icon.png",
				sizes: "32x32",
				type: "image/png"
			},
			{
				rel: "icon",
				href: "/favicon-32x32.png",
				sizes: "32x32",
				type: "image/png"
			},
			{
				rel: "icon",
				href: "/favicon-16x16.png",
				sizes: "16x16",
				type: "image/png"
			},
			{
				rel: "mask-icon",
				href: "/safari-pinned-tab.svg",
				color: "#5bbad5"
			}
		]
	},

	created() {
		// Skip interceptors for the "login" route

		// Network / Authentication error handling
		HTTP.interceptors.response.use(
			response => response,
			error => {
				NProgress.done();

				console.info("Interceptor error (url,status)", error.config.url, error.response.status);

				// Login - return all errors to original promise (store/login.vue)
				// if (error.config.url === "login" && error.response.status === 403) {
				if (error.config.url === "login") {
					return Promise.reject(error.response);
				}

				// Auth error on request - Hijack promise response
				if (error.response && [401].indexOf(error.response.status) !== -1) {
					console.warn("AUTH ERROR (interceptor)");

					this.$toasted.global.app_error({
						message: "Votre session a expirée, veuillez vous reconnecter."
					});

					this.$store.dispatch("AUTH_LOGOUT").then(() => this.$router.push("/login"));

					return;
				}

				// TODO: network error
				// TODO: 404

				// return Promise.reject(error); // error.response?
				return Promise.reject(error.response);

				// Cancelling a promise?
				// https://github.com/axios/axios/issues/583
				// return new Promise(() => {});
			}
		);

		// Authentification : si un JWT est enregistré en localStorage, on le passe dans les headers de chaque requêtes.
		if (this.$store.state.userToken)
			HTTP.defaults.headers.common.Authorization = "Bearer " + this.$store.state.userToken;

		// Synchronisation du localStorage
		// L'application root surveille les mutations du Store (`subscribe`) et execute l'action STORAGE_SYNC pour mettre à jour le localStorage.
		this.$store.subscribe((mutation, state) => {
			this.$store
				.dispatch("STORAGE_SYNC", mutation)
				.then(response => console.info("STORAGE_SYNC done"))
				.catch(error => console.warn("STORAGE_SYNC error"));
		});

		// Auto login?
		if (this.$router.currentRoute.name !== "login") this.autoLoginUser();
	},

	mounted() {
		// Configure NProgress when the DOM is ready…
		NProgress.configure({parent: ".app-header"});
	},

	computed: {
		// Utilisateur connecté ?
		isAuthenticated: function() {
			return (
				!!this.$store.state.userToken &&
				!!this.$store.state.userData &&
				this.$store.state.userData.registration_complete
			);
		}
	},

	methods: {
		// Connexion automatique si un token est présent mais sans donnée utilisateur
		autoLoginUser: function() {
			if (!this.$store.state.userToken || !!this.$store.state.userData) return;
			console.info("autoLoginUser");

			this.$store
				.dispatch("USER_LOGIN")
				.then(() => this.$router.push("dashboard"))
				.catch(error => {
					this.$router.push("login");

					// Le compte n'est pas activé (email opt-in)
					if (error.response.status === 403)
						return this.$toasted.global.app_error({
							message: "<div>Votre compte n’est pas activé, <strong>vérifiez votre boite mail.</strong></div>"
						});
					// Erreur d'authentification
					else
						return this.$toasted.global.app_error({
							message: "Impossible de vous connecter,<br>vérifiez votre identifiant et votre mot de passe !"
						});

					// Session expirée
					// this.$toasted.global.app_error({message: "Votre session a expirée, veuillez vous reconnecter."});
				});
		}
	}
}).$mount("#app");
